/** Shared, versioned Experience question contract. Missing type means legacy short text. */
export type ExperienceQuestion = {
  id: string;
  label: string;
  required: boolean;
  shortCode?: string;
  type?: "short_text" | "long_text" | "single_select" | "multi_select";
  options?: Array<{
    id: string;
    label: string;
    visitTagId?: string;
    visitTagLabel?: string;
  }>;
  additionalTextPrompt?: string;
  libraryTemplate?: "occasion" | "mobility" | "dietary";
};

export const questionTypes = [
  ["short_text", "Single line text"], ["long_text", "Long written answer"],
  ["single_select", "Single select"], ["multi_select", "Multiple select"],
] as const;

export const questionTemplates: Array<{ id: NonNullable<ExperienceQuestion["libraryTemplate"]>; name: string; question: Omit<ExperienceQuestion, "id" | "libraryTemplate"> }> = [
  { id: "occasion", name: "Occasion", question: { label: "Are you celebrating a special occasion?", shortCode: "Occasion", type: "single_select", required: false, options: [{ id: "birthday", label: "Birthday" }, { id: "anniversary", label: "Anniversary" }, { id: "other", label: "Other" }], additionalTextPrompt: "Anything else about your celebration?" } },
  { id: "dietary", name: "Dietary restrictions", question: { label: "Does anyone in your party have dietary restrictions or allergies?", shortCode: "Dietary", type: "long_text", required: false } },
  { id: "mobility", name: "Mobility", question: { label: "Are there any mobility or accessibility needs we should know about?", shortCode: "Mobility", type: "long_text", required: false } },
];

export type QuestionnaireTiming = "at_booking" | "after_booking";
export type QuestionnaireDays = 1 | 2 | 3 | 5 | 7 | 14 | 30;
export type QuestionnaireSettings = {
  timing: QuestionnaireTiming;
  introduction: string;
  expiresDaysBefore: QuestionnaireDays | null;
  reminderDaysBefore: QuestionnaireDays | null;
};

export const questionnaireDayOptions: QuestionnaireDays[] = [1, 2, 3, 5, 7, 14, 30];

const defaultQuestionnaireSettings: QuestionnaireSettings = {
  timing: "at_booking",
  introduction: "",
  expiresDaysBefore: null,
  reminderDaysBefore: null,
};

/** Read the optional questionnaire policy without changing legacy meaning. */
export function questionnaireSettings(policy: Record<string, unknown>): QuestionnaireSettings {
  const raw = policy.questionnaire;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ...defaultQuestionnaireSettings };
  const questionnaire = raw as Record<string, unknown>;
  const timing = questionnaire.timing === "after_booking" ? "after_booking" : "at_booking";
  const expiresDaysBefore = isQuestionnaireDay(questionnaire.expiresDaysBefore)
    ? questionnaire.expiresDaysBefore
    : null;
  const reminderDaysBefore = timing === "after_booking" && isQuestionnaireDay(questionnaire.reminderDaysBefore)
    ? questionnaire.reminderDaysBefore
    : null;
  return {
    timing,
    introduction: typeof questionnaire.introduction === "string" ? questionnaire.introduction : "",
    expiresDaysBefore,
    reminderDaysBefore,
  };
}

function isQuestionnaireDay(value: unknown): value is QuestionnaireDays {
  return questionnaireDayOptions.includes(value as QuestionnaireDays);
}

/** Validate the stored policy before publishing an experience version. */
export function questionnaireConfigurationError(policy: Record<string, unknown>): string | undefined {
  const raw = policy.questionnaire;
  if (raw === undefined) return undefined;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return "Questionnaire settings are invalid.";
  const questionnaire = raw as Record<string, unknown>;
  const timing = questionnaire.timing;
  if (timing !== "at_booking" && timing !== "after_booking") return "Choose when guests answer the questions.";
  if (questionnaire.introduction !== undefined && (typeof questionnaire.introduction !== "string" || questionnaire.introduction.length > 5000)) return "The question introduction must be 5,000 characters or fewer.";
  if (questionnaire.expiresDaysBefore !== undefined && questionnaire.expiresDaysBefore !== null && !isQuestionnaireDay(questionnaire.expiresDaysBefore)) return "Choose a valid question expiration window.";
  if (questionnaire.reminderDaysBefore !== undefined && questionnaire.reminderDaysBefore !== null && !isQuestionnaireDay(questionnaire.reminderDaysBefore)) return "Choose a valid unanswered reminder window.";
  if (timing === "at_booking" && questionnaire.expiresDaysBefore != null) return "Question expiration is available only for after-booking questions.";
  if (timing === "at_booking" && questionnaire.reminderDaysBefore != null) return "Unanswered reminders are available only for post-booking questions.";
  const expiresDaysBefore = questionnaire.expiresDaysBefore == null ? 0 : questionnaire.expiresDaysBefore;
  const reminderDaysBefore = questionnaire.reminderDaysBefore;
  if (reminderDaysBefore != null && typeof expiresDaysBefore === "number" && reminderDaysBefore <= expiresDaysBefore) return "The unanswered reminder must be before the answer deadline (a larger number of days before the visit).";
}

export const isChoiceQuestion = (question: ExperienceQuestion) => question.type === "single_select" || question.type === "multi_select";
export const questionInputName = (id: string) => `question:${id}`;

export function questionConfigurationError(questions: ExperienceQuestion[]): string | undefined {
  if (questions.length > 20) return "You can add up to 20 questions.";
  const ids = new Set<string>();
  for (const [index, question] of questions.entries()) {
    const prefix = `Question ${index + 1}`;
    if (!/^[a-zA-Z0-9_-]{1,80}$/.test(question.id) || question.id.endsWith("__note") || ids.has(question.id)) return `${prefix} needs a unique question ID.`;
    ids.add(question.id);
    if (!question.label.trim() || question.label.length > 500) return `${prefix} needs a question of up to 500 characters.`;
    if (question.shortCode !== undefined && (!question.shortCode.trim() || question.shortCode.length > 32)) return `${prefix}'s short code must contain 1 to 32 characters.`;
    if (isChoiceQuestion(question)) {
      if (!question.options?.length || question.options.length > 20) return `${prefix} needs between 1 and 20 choices.`;
      const optionIds = new Set<string>();
      for (const option of question.options) {
        if (!/^[a-zA-Z0-9_-]{1,80}$/.test(option.id) || optionIds.has(option.id) || !option.label.trim() || option.label.length > 200) return `${prefix} needs unique choices with labels of up to 200 characters.`;
        if (option.visitTagId !== undefined && (!option.visitTagId.trim() || option.visitTagId.length > 120)) return `${prefix} has an invalid visit tag mapping.`;
        if (option.visitTagLabel !== undefined && (typeof option.visitTagLabel !== "string" || option.visitTagLabel.length > 120)) return `${prefix} has an invalid visit tag label.`;
        optionIds.add(option.id);
      }
      if (question.additionalTextPrompt !== undefined && (!question.additionalTextPrompt.trim() || question.additionalTextPrompt.length > 500)) return `${prefix}'s additional prompt must contain 1 to 500 characters.`;
    }
  }
}

export function collectQuestionAnswers(questions: ExperienceQuestion[], form: FormData): Record<string, string> {
  return Object.fromEntries(questions.flatMap((question) => {
    const key = questionInputName(question.id);
    const answer = question.type === "multi_select" ? JSON.stringify(form.getAll(key).map(String)) : String(form.get(key) ?? "");
    return [[question.id, answer], ...(isChoiceQuestion(question) && question.additionalTextPrompt ? [[`${question.id}__note`, String(form.get(`${key}__note`) ?? "")]] : [])];
  }));
}

export function questionAnswersError(questions: ExperienceQuestion[], answers: Record<string, string>): string | undefined {
  for (const question of questions) {
    const raw = answers[question.id] ?? "";
    if (isChoiceQuestion(question)) {
      let selections: unknown = raw ? [raw] : [];
      if (question.type === "multi_select") { try { selections = JSON.parse(raw || "[]"); } catch { return `Select valid choices for ${question.label}`; } }
      if (!Array.isArray(selections) || selections.some((id) => typeof id !== "string" || !question.options?.some((option) => option.id === id)) || new Set(selections).size !== selections.length) return `Select valid choices for ${question.label}`;
      if (question.required && !selections.length) return `Please answer: ${question.label}`;
    } else {
      if (question.required && !raw.trim()) return `Please answer: ${question.label}`;
      if (raw.length > (question.type === "long_text" ? 5000 : 500)) return `The answer to ${question.label} is too long.`;
    }
    if ((answers[`${question.id}__note`]?.length ?? 0) > 5000) return `The additional note for ${question.label} is too long.`;
  }
}

/** Use the booked snapshot, never today's question definitions, to interpret answers. */
export function questionAnswerRows(questions: ExperienceQuestion[] = [], answers: Record<string, string>) {
  const consumed = new Set<string>();
  const rows = questions.flatMap((question) => {
    consumed.add(question.id);
    consumed.add(`${question.id}__note`);
    const raw = answers[question.id] ?? "";
    let answer = raw;
    if (isChoiceQuestion(question)) {
      let selected: unknown = raw ? [raw] : [];
      if (question.type === "multi_select") { try { selected = JSON.parse(raw || "[]"); } catch { selected = []; } }
      answer = Array.isArray(selected) ? selected.map((id) => question.options?.find((option) => option.id === id)?.label ?? "Unknown choice").join(", ") : "";
    }
    const label = question.shortCode?.trim() || question.label;
    const isDietary = question.libraryTemplate === "dietary" || /diet|allerg/i.test(`${question.id} ${label}`);
    return [{ id: question.id, label, answer, isDietary }, ...(question.additionalTextPrompt && answers[`${question.id}__note`] ? [{ id: `${question.id}__note`, label: `${label} — ${question.additionalTextPrompt}`, answer: answers[`${question.id}__note`], isDietary }] : [])];
  });
  return [...rows, ...Object.entries(answers).filter(([id]) => !consumed.has(id)).map(([id, answer]) => ({ id, label: id.replaceAll(/[_-]/g, " "), answer, isDietary: /diet|allerg/i.test(id) }))];
}
